import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function PanelBeatersPage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: '',
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocationFilter ? `in ${locationLabel}` : 'in South Africa';

    const titleLocationLabel = hasLocationFilter ? `in ${locationLabel}` : 'in South Africa';

    const pageTitle = hasLocationFilter
        ? `Panel Beaters in ${locationLabel} | Accident Repair & Body Shop`
        : 'Panel Beaters in South Africa | Accident Repair & Body Shop';

    const pageDescription = hasLocationFilter
        ? `Find trusted panel beaters and accident repair specialists in ${locationLabel}. Compare quotes and services for vehicle body repairs, spray painting, dent removal, and rust repairs near you.`
        : 'Find trusted panel beaters and accident repair specialists in South Africa. Browse bodyshops, spray painters, and dent removal services near me. Search by suburb or category now.';

    const baseUrl = `${ENV.DOMAIN_URL}/panel-beaters-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Soweto', 'Midrand'],
        Pretoria: ['Centurion', 'Hatfield', 'Menlyn', 'Brooklyn', 'Silverton'],
        'Cape Town': ['Bellville', 'Century City', 'Milnerton', 'Woodstock', 'Somerset West'],
        Durban: ['Umhlanga', 'Pinetown', 'Westville', 'Berea', 'Amanzimtoti'],
        Gqeberha: ['Walmer', 'Newton Park', 'Summerstrand', 'Korsten', 'Uitenhage'],
        Bloemfontein: ['Universitas', 'Langenhoven Park', 'Brandwag', 'Westdene', 'Hilton'],
    };

    const suburbLinks = cityName && suburbLinksByCity[cityName] ? suburbLinksByCity[cityName] : [];
    const locationKeywords = hasLocationFilter ? locationLabel : 'South Africa';
    const keywordList = [
        `panel beaters ${locationKeywords}`,
        `accident repair ${locationKeywords}`,
        `body shop ${locationKeywords}`,
        `spray painting ${locationKeywords}`,
        `panel beaters near me ${locationKeywords}`,
        `dent removal ${locationKeywords}`,
        `car body repairs ${locationKeywords}`,
    ].join(', ');

    const faqItems = [
        {
            question: `How do I find panel beaters ${locationSuffix}?`,
            answer: hasLocationFilter
                ? `You are filtering for ${locationLabel}. Use the province, city, or suburb filters to widen or narrow results, or clear filters to see all panel beaters.`
                : 'Use the province, city, or suburb filters to focus results. You can also browse panel beaters near me to prioritize nearby listings.',
        },
        {
            question: `What services do panel beaters offer ${locationSuffix}?`,
            answer: 'Panel beaters typically offer accident repair, panel beating, spray painting, dent and scratch removal, rust repairs, bumper repairs, windscreen replacement, and insurance claim assistance. Many also offer free quotes.',
        },
        {
            question: 'How do I choose the right panel beater?',
            answer: 'Look for panel beaters with insurance approval, positive reviews, and a track record of quality workmanship. Ask for a written quote, check the warranty on repairs, and confirm they use quality paint and materials.',
        },
        {
            question: `Can I compare panel beaters ${locationSuffix}?`,
            answer: 'Yes. Shortlist multiple panel beaters, compare their services and locations, and contact them directly for quotes before deciding. Many panel beaters offer free assessments.',
        },
        {
            question: 'Will my insurance cover panel beating repairs?',
            answer: 'Most comprehensive insurance policies cover accident damage including panel beating repairs. Check with your insurer for approved repairers. Many panel beaters on this directory are insurance approved and can assist with claims.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywordList} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'LocalBusiness',
                                    '@id': canonicalUrl,
                                    name: `Panel Beaters ${titleLocationLabel}`,
                                    description: pageDescription,
                                    url: canonicalUrl,
                                    areaServed: locationKeywords,
                                    knowsAbout: [
                                        'Panel Beating',
                                        'Accident Repair',
                                        'Spray Painting',
                                        'Dent Removal',
                                        'Rust Repairs',
                                        'Car Body Repairs',
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: {
                                            '@type': 'Answer',
                                            text: item.answer,
                                        },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>
            <section className="relative overflow-hidden bg-[#100800] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(249, 115, 22, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(234, 88, 12, 0.3), transparent 40%), radial-gradient(circle at 65% 85%, rgba(251, 146, 60, 0.25), transparent 45%)",
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.3em] text-orange-200">Panel Beater Directory</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                Panel Beaters & Accident Repair {titleLocationLabel}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-orange-100">
                                Discover trusted panel beaters, body shops, and accident repair specialists with precise
                                location filters. Find quality vehicle body repairs near you.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Start filtering
                                </Link>
                                <Link
                                    href="/panel-beaters-near-me"
                                    className="bg-orange-500 text-white px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Panel beaters near me
                                </Link>
                                <span className="text-xs text-orange-100">New: quick city shortcuts below.</span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-orange-100">Quick start searches</p>
                                    <span className="rounded-full bg-orange-500/20 px-2.5 py-1 text-xs text-orange-100">
                                        Popular
                                    </span>
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <Link
                                        href="/panel-beaters-near-me/panel-beaters/gauteng/johannesburg"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-orange-300"
                                    >
                                        Panel Beaters Johannesburg
                                    </Link>
                                    <Link
                                        href="/panel-beaters-near-me/panel-beaters/western-cape/cape-town"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-orange-300"
                                    >
                                        Panel Beaters Cape Town
                                    </Link>
                                    <Link
                                        href="/panel-beaters-near-me/panel-beaters/kwazulu-natal/durban"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-orange-300"
                                    >
                                        Panel Beaters Durban
                                    </Link>
                                    <Link
                                        href="/panel-beaters-near-me/panel-beaters/gauteng/pretoria"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-orange-300"
                                    >
                                        Panel Beaters Pretoria
                                    </Link>
                                </div>
                                <div className="mt-5 grid gap-3 sm:grid-cols-3 text-xs text-orange-100">
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Accident repairs</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Spray painting</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Insurance approved</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-2xl font-semibold text-slate-900">Find Trusted Panel Beaters & Accident Repair Near Me</h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Looking for panel beaters {locationSuffix}? CarSparesEzyFind helps you connect
                                with verified panel beaters, body shops, and accident repair specialists across South Africa.
                                From minor dents and scratches to full accident repairs and spray painting, use the filters
                                to narrow by province, city, or suburb, then compare services and supplier profiles.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Whether you need panel beaters near me in Sandton, accident repair in
                                Roodepoort, or a body shop in Randburg, this directory is designed to surface the most
                                relevant options. Start with a broad location, then refine to specific suburbs to find the
                                nearest repair centres. Many panel beaters are insurance approved and can assist with claims.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Popular searches include panel beaters Johannesburg Gauteng, accident repair Cape Town,
                                panel beaters in City of Johannesburg, and local body shops Gauteng.
                                Whether you need dent removal, rust repairs, bumper replacement, or a full respray, you can
                                shortlist specialists and get quotes with confidence.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Look for panel beaters with strong reviews, insurance company approval, and transparent
                                pricing. Many shops offer free assessments and written quotes. Always ask about the warranty
                                on repairs and confirm paint quality before proceeding.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-slate-900">Services Offered</h3>
                            <ul className="mt-4 space-y-2 text-sm text-slate-600">
                                <li>Accident & collision repair</li>
                                <li>Panel beating & straightening</li>
                                <li>Spray painting & resprays</li>
                                <li>Dent & scratch removal</li>
                                <li>Rust repairs & treatment</li>
                                <li>Bumper repairs & replacement</li>
                                <li>Windscreen replacement</li>
                                <li>Insurance claim assistance</li>
                                <li>Paintless dent repair (PDR)</li>
                                <li>Colour matching</li>
                                <li>Fiberglass repairs</li>
                                <li>Tow-in service</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">Browse panel beaters by city</h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Fast links to panel beaters in major cities and metro areas.
                                    </p>
                                </div>
                                <Link
                                    href="/panel-beaters-near-me"
                                    className="text-sm font-semibold text-orange-700"
                                >
                                    View all cities
                                </Link>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickCities.map((city) => (
                                    <Link
                                        key={city.name}
                                        href={`/panel-beaters-near-me/panel-beaters/${city.province}/${slugify(city.name)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-orange-500 hover:text-orange-700"
                                    >
                                        {city.name}
                                    </Link>
                                ))}
                            </div>
                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 1</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Choose location</p>
                                    <p className="mt-1 text-xs text-slate-600">Select province, city, or suburb.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 2</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Compare quotes</p>
                                    <p className="mt-1 text-xs text-slate-600">Request quotes from multiple shops.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 3</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Book repairs</p>
                                    <p className="mt-1 text-xs text-slate-600">Confirm quote and book your repair.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Browse by province</h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Filter by province, then drill down to city and suburb.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickProvinces.map((province) => (
                                    <Link
                                        key={province}
                                        href={`/panel-beaters-near-me/panel-beaters/${slugify(province)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-orange-500 hover:text-orange-700"
                                    >
                                        {province}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50 p-5">
                            <h3 className="text-sm font-semibold text-slate-900">Repair tips</h3>
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                <li>Always get at least 2–3 written quotes before committing.</li>
                                <li>Ask if the panel beater is approved by your insurer.</li>
                                <li>Check the warranty offered on all repairs and paint work.</li>
                                <li>Confirm OEM vs aftermarket parts will be used.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10" aria-labelledby="suburb-links-heading">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 id="suburb-links-heading" className="text-xl font-semibold text-slate-900">
                        {cityName
                            ? `Panel Beaters by Suburb in ${cityName}`
                            : 'Panel Beaters by City in South Africa'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        {cityName
                            ? `Explore panel beaters and body shops in ${cityName} suburbs to find the most convenient repair centre.`
                            : 'Jump to popular cities and metros to find panel beaters near me.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {cityName && suburbLinks.length > 0
                            ? suburbLinks.map((suburb) => (
                                  <Link
                                      key={suburb}
                                      href={`/panel-beaters-near-me?city=${slugify(cityName)}&suburb=${slugify(suburb)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-orange-500 hover:text-orange-700"
                                  >
                                      {`Panel Beaters in ${suburb}`}
                                  </Link>
                              ))
                            : quickCities.map((city) => (
                                  <Link
                                      key={city.name}
                                      href={`/panel-beaters-near-me/panel-beaters/${city.province}/${slugify(city.name)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-orange-500 hover:text-orange-700"
                                  >
                                      {`Panel Beaters in ${city.name}`}
                                  </Link>
                              ))}
                    </div>
                </div>
            </section>

            <div className="my-10">
                <div className="container" id="business-filters">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds="1396"
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-orange-700">Panel Beater Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                Panel beaters & body shops {locationSuffix}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Use this page to find panel beaters near you, or drill into a specific province,
                                city, or suburb. Results update instantly as you apply filters.
                            </p>
                            <p className="mt-4 text-sm text-slate-600">
                                Browse local panel beaters, body shops, spray painters, and accident repair centres in
                                one place. Save time by comparing locations, services, and quotes side-by-side.
                            </p>
                            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                                {hasLocationFilter
                                    ? `Showing location filters for ${locationLabel}. Change province, city, or suburb to explore nearby areas.`
                                    : 'Tip: Start with a province, then narrow by city or suburb for tighter local results.'}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">
                                Frequently Asked Questions (Panel Beaters SEO)
                            </h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <DownloadApp />
        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: '1396',
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';

    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
